import {Button, RemoveTab, Table, Toggle} from "omoplay";
import SuspensePopup from "../../SuspensePopup";
import EventSimpleList from "./EventSimpleList";
import {useEffect, useMemo, useRef, useState} from "react";
import {
    deleteProjectDuplicate,
    deleteProjectEntryPoint,
    deleteProjectEvent, getDetailDuplicateProjects,
    getDetailEventsToProject, postProjectDuplicates,
    postProjectEntryPoint,
    postProjectEvent, turnProject
} from "../../../api/project/project.api";
import GrayButton from "../../GrayButton";
import EntrypointSimpleList from "./EntrypointSimpleList";
import ProjectDetailPeriod from "./ProjectDetailPeriod";
import ProjectDetailBenefitPassword from "./ProjectDetailBenefitPassword";
import ProjectDetailDuplicate from "./ProjectDetailDuplicate";
import EntryPointPreviewPopup from "../entrypoint/EntryPointPreviewPopup";
import BenefitPreview from "../../BenefitPreview";
import InputGroup from "../InputGroup";
import ProjectDetailMetaTag from "./ProjectDetailMetaTag";
import ProjectDetailCompleteRedirect from "./ProjectDetailCompleteRedirect";
import ProjectDetailCheckNewUser from "./ProjectDetailCheckNewUser";
import ProjectDetailPrecondition from "./ProjectDetailPrecondition";


/** 이 개수를 넘으면 셀에 전량 렌더하지 않고 접는다(요약 + 관리 팝업). */
const ENTRYPOINT_INLINE_LIMIT = 10

const ProjectDetailSummary = ({projectId, project, passwordType, benefitPassword}) => {
    const eventHeaders = [
        { name: "이벤트명" }, { name: "혜택", unsetPadding: true }, { name: "진입점", unsetPadding: true },
        { name: "삭제", align: "center", width: "100px" }
    ]
    const [showEvents, setShowEvents] = useState(false)
    const [getEventIdAndShowEntryPoints, setGetEventIdAndShowEntryPoints] = useState(false)
    const [selectedEvents, setSelectedEvents] = useState([])
    const [renderEvents, setRenderEvents] = useState([])
    const [isEventChanged, setEventChanged] = useState(false)
    const [duplicateProjects, setDuplicateProjects] = useState([])
    const [isDuplicatesChanged, setDuplicatesChanged] = useState(false)
    const [showEntryPointAndGetId, setShowEntryPointAndGetId] = useState()
    /* 진입점 팝업을 '연결된 것만 보기' 상태로 열지 여부.
       🔴 종전에는 '+ 추가하기' 와 '전체 보기 · 관리' 가 **서로 다른 팝업**이었다
          (`EntrypointSimpleList` vs `ConnectedEntrypointsPopup`) → 검색·표·페이지네이션·푸터가 전부 달랐다.
          사용자 지적("두 팝업이 기본적으로 동일해야 한다", 2026-09-03) ⇒ **하나로 합쳤다.**
          '전체 보기 · 관리' = 같은 팝업 + 이 필터 ON 으로 여는 것뿐이다. */
    const [pickerConnectedOnly, setPickerConnectedOnly] = useState(false)
    // 진입점 추가 팝업에서 고른 목록. 확정은 팝업 **푸터의 '확인'** 이 한다(자세한 이유 = 아래 SuspensePopup 주석).
    const [pendingEntryPoints, setPendingEntryPoints] = useState([])
    // 같은 팝업에서 **체크를 풀어 해제하기로** 표시한 목록(아직 시작하지 않은 프로젝트에서만 가능)
    const [pendingRemovals, setPendingRemovals] = useState([])
    // 확정 진행 상태. 서버에 일괄 API 가 없어 1건씩 순차 호출하므로 버튼에 진행률을 보여준다.
    const [applying, setApplying] = useState(null)
    const [showPreviewPopupAndGetId, setShowPreviewPopupAndGetId] = useState(undefined);

    /* 진입점 추가 팝업이 열린 **그 이벤트**에 이미 연결된 진입점.
       🔴 종전에는 `allEntryPoints` 라는 별도 state 에 모든 이벤트의 진입점을 누적하려 했지만,
          `setAllEntryPoints([...allEntryPoints, ...])` 를 map 안에서 호출해 stale state 를 읽었다
          → 실제로는 **마지막 이벤트의 진입점만** 남아, 어느 이벤트의 팝업을 열어도 그 값이 '이미 연결'로
          쓰였다(다른 이벤트에 연결된 진입점이 '연결됨'으로 잠기거나, 반대로 이미 연결된 것이 안 잠겼다).
          `selectedEvents` 에 이미 이벤트별 entryPoints 가 들어 있으므로 그것을 그대로 쓴다. */
    /* useMemo = 이 배열의 identity 가 렌더마다 바뀌면 자식(`EntrypointSimpleList`)의
       `connectedIds`·`selectable` useMemo 가 매 렌더 609건을 다시 걸러낸다(체크박스 클릭마다). */
    const pickerEvent = useMemo(
        () => selectedEvents.find(event => event.id === getEventIdAndShowEntryPoints),
        [selectedEvents, getEventIdAndShowEntryPoints]
    )
    const connectedEntryPointIds = useMemo(
        () => pickerEvent?.entryPoints?.map(entryPoint => entryPoint.id) ?? [],
        [pickerEvent]
    )

    /* 이 프로젝트가 진입점을 **점유**하는가 = 시작 토글이 켜져 있고 아직 종료되지 않았다.
       백엔드 `ProjectService.occupiesEntryPoints`(`start && 종료일 미도래`)와 같은 정의다.
       🔴 해제를 **막는 데 쓰지 않는다** — 막는 것은 서버뿐이고(이벤트의 마지막 1건이면 거부),
          여기서는 "지금 빼면 그 QR 이 바로 종료 안내로 바뀐다" 를 알릴지만 정한다.
       🔧 `project.status` 는 쓸 수 없다 — `ProjectDetailResponse` 에 그 필드가 없다(`statusKor` 뿐).
          종료 여부를 `statusKor` 로 보는 것은 보수적 판정이다(애매하면 확인창을 띄우는 쪽). */
    const occupiesEntryPoints = project.start === true && project.statusKor !== "종료"

    /* 🔴 이벤트 목록의 **최신값**을 ref 로 든다(2026-09-23).
       아래 useEffect 가 `setSelectedEvents(events)` 와 `setRenderEvents(...)` 를 같은 콜백에서 부르는데,
       표 안의 onClick 이 잡는 `entryPointDelete`/`eventDelete` 는 **그 effect 를 돌린 렌더의** 함수라
       그 렌더의 `selectedEvents` 는 아직 이전 값이다. 로드 직후 첫 클릭에서 "남은 진입점" 계산이
       한 박자 늦은 목록을 보게 되므로 state 대신 ref 에서 읽는다. */
    const eventsRef = useRef([])

    /**
     * 진입점을 빼면 그 이벤트/프로젝트가 어떻게 되는지. 빈 문자열 = 특별히 알릴 것 없음.
     * 🔴 2026-09-23 — 서버의 "마지막 진입점은 뺄 수 없다" 가드를 없앴다. `Project.status()` 가
     *    "진입점 있는 이벤트가 최소 1개" 로 바뀌어 빈 이벤트는 비활성일 뿐 프로젝트를 멈추지 않는다.
     *    다만 **프로젝트 전체에 진입점이 남지 않으면** '설정 필요' 가 되어 멈추므로 그것만은 분명히 알린다.
     */
    const detachConsequence = (eventId, removingIds) => {
        const events = eventsRef.current
        const event = events.find(e => e.id === eventId)
        if (!event) return ""
        const remainingHere = event.entryPoints.filter(ep => !removingIds.includes(ep.id)).length
        if (remainingHere > 0) return ""
        const othersRunnable = events.some(e => e.id !== eventId && e.entryPoints.length > 0)
        return othersRunnable
            ? "이 이벤트의 마지막 진입점입니다. 해제하면 이 이벤트는 어떤 QR 에서도 열리지 않습니다.\n" +
              "(프로젝트의 다른 이벤트는 그대로 진행됩니다.)"
            : "🔴 이 프로젝트에 진입점이 남지 않습니다. 해제하면 프로젝트가 '설정 필요' 상태가 되어 멈춥니다."
    }

    /** 이벤트를 프로젝트에서 뺄 때의 결과. 데이터는 지워지지 않는다 — 표시와 QR 동작만 바뀐다. */
    const eventDeleteConsequence = (eventId) => {
        const events = eventsRef.current
        const event = events.find(e => e.id === eventId)
        const spotCount = event?.entryPoints?.length ?? 0
        const othersRunnable = events.some(e => e.id !== eventId && e.entryPoints.length > 0)
        const lines = [`이벤트 "${event?.name ?? eventId}" 을(를) 이 프로젝트에서 뺍니다.`]
        if (spotCount > 0) lines.push(`연결된 진입점 ${spotCount}건의 QR 은 바로 종료 안내 페이지로 바뀝니다(다른 이벤트에 연결돼 있으면 그쪽으로 이어집니다).`)
        lines.push(othersRunnable
            ? "프로젝트의 다른 이벤트는 그대로 진행됩니다."
            : "🔴 진입점이 있는 이벤트가 남지 않아 프로젝트가 '설정 필요' 상태가 되어 멈춥니다.")
        lines.push("이미 발급된 혜택과 참여·성과 기록은 그대로 남지만, 프로젝트 상세의 혜택 통계·수량 설정에서는 이 이벤트의 혜택이 더 이상 표시되지 않습니다.")
        lines.push("이벤트 자체는 삭제되지 않으며 이벤트 목록에 남습니다.")
        return lines.join("\n")
    }

    /** 진행중·예약 프로젝트에서 진입점을 뺄 때 반드시 함께 알려야 하는 것. */
    const runningDetachNotice = () =>
        `'${project.statusKor}' 상태의 프로젝트입니다.\n` +
        "해제한 진입점의 QR 은 바로 종료 안내 페이지로 바뀝니다.\n" +
        "(같은 프로젝트의 다른 이벤트나 다른 진행중 프로젝트에 연결돼 있으면 그쪽으로 이어집니다.)\n" +
        "이미 발급된 혜택과 지난 성과 기록은 그대로 남습니다."

    useEffect(() => {
        getDetailEventsToProject(projectId).then(({data}) => {
            const events = data.events
            eventsRef.current = events
            setSelectedEvents(events)
            setRenderEvents(events.map(event => {
                return [
                    <div className="flex-align-center gap-2">
                        <div className="pointer text-primary"
                             onClick={() => window.location.href = `/event/edit?id=${event.id}`}>
                            {event.name}
                        </div>
                    </div>,
                    <div className="child-full" style={{borderBottom: "unset"}}>
                        <div>
                            {
                                event.benefits.map(benefit => {
                                    return (
                                        <div className={`table-column-border${benefit.id ? " pointer text-primary" : ""}`}
                                        onClick={() => {
                                            setShowPreviewPopupAndGetId(benefit.id)
                                        }}>
                                            {benefit.name} ({benefit.winningPercent}%)
                                        </div>
                                    )
                                })
                            }
                        </div>
                    </div>,
                    <div className="child-full">
                        <div className="w-full">
                            {
                                /* 진입점이 많으면 접는다. 종전에는 전량을 이 셀에 렌더해서
                                   609건 고객사에서는 셀이 609행 높이로 늘어나 화면을 쓸 수 없었다.
                                   ENTRYPOINT_INLINE_LIMIT 이하는 종전과 동일하게 인라인 표시 → 기존 화면 무변화. */
                                event.entryPoints.length > ENTRYPOINT_INLINE_LIMIT ? (
                                    /* 🔴 `table-column-border` 가 아니라 `table-column-block` 이다 —
                                       앞의 것은 `height:44px` 고정이라 3줄(제목·이름·버튼)이 상자 밖으로
                                       흘러나와 아래 '+ 추가하기' 와 겹쳐 보였다(common.css 주석 참조). */
                                    <div className="table-column-block flex-col gap-2">
                                        <div className="font-semibold">
                                            진입점 {event.entryPoints.length}건 연결됨
                                        </div>
                                        <div className="text-sm text-gray-500 truncate">
                                            {event.entryPoints.slice(0, 3).map(ep => ep.name).join(", ")}
                                            {event.entryPoints.length > 3 && ` 외 ${event.entryPoints.length - 3}건`}
                                        </div>
                                        <div>
                                            <Button variants="default" value="전체 보기 · 관리"
                                                    onClick={() => {
                                                        setPickerConnectedOnly(true)
                                                        setGetEventIdAndShowEntryPoints(event.id)
                                                    }}
                                            />
                                        </div>
                                    </div>
                                ) : event.entryPoints.map(entryPoint => {
                                    return (
                                        <div className="flex-align-center justify-between table-column-border">
                                            <div className="pointer text-primary"
                                                 onClick={() => setShowEntryPointAndGetId(entryPoint.id)}>
                                                {entryPoint.name}
                                            </div>
                                            <div>
                                                {/* 🔴 2026-09-22: `project.disabledDelete` 게이트를 제거했다.
                                                    종전에는 한 번이라도 시작/종료한 프로젝트(`modified_turn_at != null`)에서
                                                    이 버튼이 **사라져** 진입점을 미는 유일한 방법이 DB 직접 수정이었다.
                                                    추가(`+ 추가하기`)에는 게이트가 없어 "넣는 것만 되고 빼는 것은 안 된다" 였다.
                                                    위험한 경우(이벤트의 마지막 진입점)는 **서버가 거부**하고,
                                                    진행중·예약 프로젝트는 아래 `entryPointDelete` 가 확인창을 띄운다.
                                                    근거 = `_ai-runs/2026-09-22_005_started_project_entrypoint_unlink/001_analysis.md` */}
                                                <GrayButton className="px-1.5"
                                                            onClick={() =>
                                                                entryPointDelete(event.id, entryPoint.id)}>
                                                    X
                                                </GrayButton>
                                            </div>
                                        </div>
                                    )
                                })
                            }
                        </div>
                        <div className="flex-center-all w-full" style={{height: "44px"}}
                             onClick={() => {
                                 setPickerConnectedOnly(false)   // 추가하러 왔으면 전체 목록으로 시작한다
                                 setGetEventIdAndShowEntryPoints(event.id)
                             }}>
                            <div className="flex-center-all w-full pointer">
                                + 추가하기
                            </div>
                        </div>
                    </div>,
                    <div className="flex-center-all h-full">
                        {/* 🔴 2026-09-23: `project.disabledDelete` 게이트를 제거했다(진입점 X 와 같은 이유).
                            결과는 `eventDelete` 의 확인창이 설명한다. 분석 =
                            `_ai-runs/2026-09-22_005_started_project_entrypoint_unlink/008_last_entrypoint_and_event_removal.md` */}
                        <div className="pointer" onClick={() => eventDelete(event.id)}>
                            <RemoveTab />
                        </div>
                    </div>
                ]
            }))
            setEventChanged(false)
        })
    }, [project.disabledDelete, occupiesEntryPoints, project.statusKor, projectId, isEventChanged]);

    useEffect(() => {
        getDetailDuplicateProjects(projectId).then(({data}) => {
            setDuplicateProjects(data.duplicateProjects)
            setDuplicatesChanged(false)
        })
    }, [projectId, isDuplicatesChanged])

    const eventSave = (eventId) => {
        postProjectEvent(projectId, eventId).then(({data}) => {
            if (data === "fail") {
                alert("이벤트를 추가할 수 없습니다. 기간이 없는 혜택이 있습니다.\n이벤트 설정에서 혜택에 기간을 정해주시거나 프로젝트의 종료기간을 설정 하세요.")
            }
            setEventChanged(true)
        })
    }

    const eventDelete = (eventId) => {
        if (window.confirm(`${eventDeleteConsequence(eventId)}\n\n계속할까요?`)) {
            deleteProjectEvent(projectId, eventId).then(() => {
                setEventChanged(true)
            })
        }
    }
    /**
     * 진입점 추가 팝업의 확정. 추가와 해제를 **한 번에** 적용한다.
     *
     * 🔴 반드시 **순차** 호출한다. 추가/해제 모두 같은 `project.project_settings` JSON 을
     *    read-modify-write 하므로, 병렬로 쏘면 마지막 응답이 앞선 변경을 덮어쓴다
     *    (`ProjectService.entryPointSave` / `entryPointDelete` 둘 다 JSON 전체를 다시 쓴다).
     *    해제는 서버에 일괄 API 가 없어 1건당 1회다 → 609건이면 609회. 그래서 진행률을 보여준다.
     *
     * 해제를 먼저 하고 추가를 나중에 한다 — 같은 id 가 양쪽에 들어갈 수는 없지만(체크 상태가 배타적),
     * 순서를 고정해 두면 결과가 언제나 같다.
     */
    const entryPointApply = async (eventId, addEntryPointIds, removeEntryPointIds) => {
        const total = removeEntryPointIds.length + (addEntryPointIds.length > 0 ? 1 : 0)
        setApplying({done: 0, total})
        try {
            for (let i = 0; i < removeEntryPointIds.length; i++) {
                // eslint-disable-next-line no-await-in-loop
                const {data: rejected} = await deleteProjectEntryPoint(projectId, eventId, removeEntryPointIds[i])
                /* 🔴 거부되면 **거기서 멈춘다.** 계속 돌리면 같은 사유로 남은 건도 전부 실패하는데
                   화면에는 진행률만 올라가 "다 됐다" 로 보인다. 이미 처리된 건은 그대로 유지된다. */
                if (rejected) {
                    alert(`${rejected}\n\n(${i}건 해제 후 중단했습니다. 남은 ${removeEntryPointIds.length - i}건은 처리되지 않았습니다.)`)
                    return
                }
                setApplying({done: i + 1, total})
            }
            if (addEntryPointIds.length > 0) {
                // 추가는 목록 전체를 한 번에 보낸다(백엔드가 append + dedupe = 멱등)
                /* 🔴 추가도 **거부 사유를 payload 로** 돌려준다(2026-09-22, 해제와 같은 방식).
                   거부 = 같은 프로젝트의 **다른 이벤트**가 이미 그 진입점을 쓰고 있을 때.
                   응답을 흘려버리면 "연결했다고 나오는데 목록에 없다" 가 된다.
                   해제를 먼저 끝냈으므로 그 결과는 이미 반영돼 있다 → 그 사실까지 함께 알린다. */
                const {data: rejected} = await postProjectEntryPoint(projectId, eventId, addEntryPointIds)
                if (rejected) {
                    alert(removeEntryPointIds.length > 0
                        ? `${rejected}\n\n(연결 해제 ${removeEntryPointIds.length}건은 이미 적용됐습니다. 추가만 취소됐습니다.)`
                        : rejected)
                    return
                }
                setApplying({done: total, total})
            }
        } finally {
            setApplying(null)
            setEventChanged(true)
        }
    }
    const entryPointDelete = (eventId, entryPointId) => {
        /* 🔴 진행중·예약 프로젝트에서는 **확인받는다**(2026-09-22). 막지는 않는다 —
           빼는 것 자체는 정상 운영이고(이벤트 교체·지점 폐점), 위험한 경우는 서버가 거부한다.
           다만 그 QR 이 **즉시** 종료 안내로 바뀌는 것은 현장에 붙은 물건의 동작이 바뀌는 일이다. */
        const consequence = detachConsequence(eventId, [entryPointId])
        if (occupiesEntryPoints || consequence) {
            const parts = ["진입점 1건의 연결을 해제합니다."]
            if (consequence) parts.push(consequence)
            if (occupiesEntryPoints) parts.push(runningDetachNotice())
            if (!window.confirm(`${parts.join("\n\n")}\n\n계속할까요?`)) return
        }
        /* 🔴 서버가 거부하면 **거부 사유를 payload 로** 돌려준다(빈 문자열 = 성공, `/turn` 과 같은 방식).
           그것을 흘려버리면 "눌렀는데 아무 일도 안 일어난다" 는 조용한 실패가 된다.
           (2026-09-23 현재 서버가 해제를 거부하는 경우는 없다 — 계약만 유지한다.) */
        deleteProjectEntryPoint(projectId, eventId, entryPointId).then(({data}) => {
            if (data) alert(data)
            setEventChanged(true)
        })
    }
    const duplicatesSave = (duplicateProjects) => {
        postProjectDuplicates(projectId, duplicateProjects.map(p => p.id)).then(() => {
            setDuplicatesChanged(true)
        })
    }
    const duplicatesDelete = (duplicateProject) => {
        deleteProjectDuplicate(projectId, duplicateProject).then(() => {
            setDuplicatesChanged(true)
        })
    }

    const onToggleTurn = (id, flag) => {
        setTimeout(() => {
            const message = flag ? "해당 프로젝트를 시작 하시겠습니까?\n시작기간 수정 및 이벤트 수정이 불가능해 집니다." : "해당 프로젝트를 종료 하시겠습니까?"
            if (window.confirm(message)) {
                turnProject(id, flag).then(({data}) => {
                    if (data) {
                        alert(data)
                    }
                    window.location.reload()
                })
            } else {
                window.location.reload()
            }
        }, 0)
    }

    return (
        <div className="flex-col gap-2">
            <InputGroup label="프로젝트 진행" description="프로젝트를 시작하거나 종료합니다.">
                <Toggle value={project.start}
                        disabled={project.status === "NECESSARY"}
                        onToggle={(flag) => onToggleTurn(project.id, flag)} />
            </InputGroup>
            <InputGroup
                label={
                <div className="flex-align-center justify-between">
                    <div className="text-[16px] font-bold text-gray-900">첫방문 이벤트 설정</div>
                    <Button variants="dark" value="+ 첫방문 이벤트 연결" onClick={() => setShowEvents(true)} />
                </div>
                }
                description="아래 이벤트들은 중복 참여가 불가능합니다."
            >
                <div className="flex-col gap-2">
                    {/* 🔴 `table-fit-cols` = 헤더 행과 본문 행의 칸을 맞춘다.
                        omoplay Table 은 두 행이 별개의 flex 행이고 셀이 `min-width:auto` 라,
                        진입점 요약줄(`truncate` = nowrap)의 전체 텍스트 폭만큼 그 칸이 넓어져
                        가로 2080px 미만에서는 타이틀 행과 어긋났다(common.css 주석에 실측값). */}
                    <div className="table-fit-cols">
                        <Table headers={eventHeaders} data={renderEvents} emptyItem={
                            <Button variants="primary" value="+ 첫방문 이벤트 연결" onClick={() => setShowEvents(true)} />
                        } />
                    </div>
                </div>
            </InputGroup>
            <ProjectDetailBenefitPassword projectId={projectId} passwordType={passwordType} benefitPassword={benefitPassword} />
            <ProjectDetailCheckNewUser projectId={projectId} used={project.checkNewUser} />
            <ProjectDetailPrecondition projectId={projectId} survey={project.projectSettings?.survey} />
            <ProjectDetailPeriod projectId={projectId} initStartDate={project.startDate}
                                 initEndDate={project.endDate} disabledEdit={project.disabledDelete} isEdit={true}
            />
            <ProjectDetailDuplicate projectId={projectId} duplicateProjects={duplicateProjects}
                                    onChange={(duplicateProjects) => duplicatesSave(duplicateProjects)}
                                    onDelete={(duplicateProject) => duplicatesDelete(duplicateProject)}
            />
            <ProjectDetailMetaTag projectId={projectId} />
            <ProjectDetailCompleteRedirect projectId={projectId} />
            <SuspensePopup
                title={"이벤트 추가하기"}
                visible={showEvents}
                style={{width: "1200px"}}
                onClose={() => setShowEvents(false)}
            >
                <EventSimpleList excludeEventIds={selectedEvents.map(event => event.id)}
                                 onSelected={(event) => {
                                     eventSave(event.id)
                                     setShowEvents(false)
                                 }}
                />
            </SuspensePopup>
            {/* 🔴 확정/취소를 **푸터**로 옮겼다(2026-08-27).
                종전에는 내용 안에 '선택 연결' 버튼이 있고 푸터는 '닫기' 하나가 넓은 자리를 차지했다.
                이 팝업은 '수정하다 확정/폐기' 흐름이므로 apply/discard 가 맞고, 단순 닫기는
                우상단 X 로 충분하다. → 푸터 = [취소] [N건 연결]
                `modal-fit` = 흰 패널 안에 헤더·푸터 고정. 목록만 스크롤하는 것은 내용 쪽의
                `.modal-scroll-area` 가 담당한다. 둘 다 common.css 주석 참조. */}
            <SuspensePopup
                /* 🔴 타이틀도 두 입구에서 같다 — 팝업이 하나이기 때문이다.
                   이벤트명을 붙여 "어느 이벤트의 진입점인지" 를 잃지 않게 한다
                   (종전 '연결된 진입점 관리' 팝업이 제공했던 정보). */
                title={`진입점 연결 관리${pickerEvent?.name ? ` — ${pickerEvent.name}` : ""}`}
                visible={getEventIdAndShowEntryPoints}
                style={{width: "1200px"}}
                className="modal-fit"
                cancelText="취소"
                /* 🔴 라벨은 **확정 후 이 이벤트에 연결될 총 건수**다(이미 연결 − 해제 + 새로 선택).
                   추가는 백엔드가 append+dedupe(멱등)라 새로 선택한 것만 보내면 되고
                   (`ProjectService.entryPointSave` → `EntryPointMergePolicy.merge`),
                   해제는 1건씩 DELETE 다(`entryPointDelete`) → 진행률을 라벨에 보여준다.
                   이미 1건이 연결된 상태에서 열면 '총 1건 연결'(비활성) → 1개 고르면 '총 2건 연결'(활성).
                   활성 조건은 총계가 아니라 **보낼 것이 있는지**다 — a 를 빼고 b 를 고르면
                   총계가 그대로 1건이어도 활성이어야 한다(2026-09-03 사용자 요청). */
                confirmText={applying
                    ? `적용 중 ${applying.done}/${applying.total}`
                    : `총 ${connectedEntryPointIds.length - pendingRemovals.length + pendingEntryPoints.length}건 연결`}
                confirmDisabled={applying != null
                    || (pendingEntryPoints.length === 0 && pendingRemovals.length === 0)}
                onConfirm={() => {
                    if (applying) return
                    const removals = pendingRemovals.map(entryPoint => entryPoint.id)
                    /* 해제는 되돌릴 수 없고(다시 고르면 되지만 그 사이 고객 유입은 사라진다)
                       609건이면 API 609회라 시간도 걸린다 → 건수를 보여주고 한 번 묻는다.
                       🔴 진행중·예약 프로젝트면 같은 창에서 QR 이 바로 종료 안내가 된다는 것까지 알린다
                          (확인창을 두 번 띄우지 않는다). */
                    if (removals.length > 0) {
                        const parts = [`연결 해제 ${removals.length}건, 추가 ${pendingEntryPoints.length}건을 적용합니다.`]
                        // 추가분이 있으면 이벤트가 비지 않으므로 '마지막 진입점' 안내는 해제만 있을 때만 의미가 있다.
                        const consequence = pendingEntryPoints.length === 0
                            ? detachConsequence(getEventIdAndShowEntryPoints, removals) : ""
                        if (consequence) parts.push(consequence)
                        if (occupiesEntryPoints) parts.push(runningDetachNotice())
                        const message = parts.length > 1
                            ? `${parts.join("\n\n")}\n\n진행할까요?`
                            : `${parts[0]}\n진행할까요?`
                        if (!window.confirm(message)) {
                            return
                        }
                    }
                    const eventId = getEventIdAndShowEntryPoints
                    const additions = pendingEntryPoints.map(entryPoint => entryPoint.id)
                    setPendingEntryPoints([])
                    setPendingRemovals([])
                    setGetEventIdAndShowEntryPoints(undefined)
                    entryPointApply(eventId, additions, removals)
                }}
                onClose={() => {
                    if (applying) return   // 적용 중에는 닫지 않는다(중간에 끊기면 일부만 반영된다)
                    setPendingEntryPoints([])
                    setPendingRemovals([])
                    setGetEventIdAndShowEntryPoints(undefined)
                }}
            >
                <EntrypointSimpleList selectedEntrypointIds={connectedEntryPointIds}
                                      /* 🔴 2026-09-22: 종전에는 `project.disabledDelete === false`(= 아직 한 번도
                                         시작/종료하지 않은 프로젝트)에서만 해제를 허용했다. 운영 중인 프로젝트에서
                                         진입점을 빼려면 DB 를 직접 고치는 수밖에 없었고, 추가는 열려 있어
                                         "넣을 수는 있는데 뺄 수가 없다" 였다. 선행 안전장치(마지막 진입점 서버 거부 /
                                         중복 점유 검사 / 성과 축 = 연결 ∪ 이력)가 2026-09-04 에 전부 들어갔으므로 연다.
                                         상세 화면의 행별 X 버튼도 같이 열었다. */
                                      allowDisconnect={true}
                                      /* 🔴 `visible` 을 넘겨야 닫을 때 자식이 선택 상태를 버린다 —
                                         omoplay Modal 은 `forceMount` 라 닫아도 언마운트되지 않는다. */
                                      visible={getEventIdAndShowEntryPoints != null}
                                      initialConnectedOnly={pickerConnectedOnly}
                                      /* 자기 자신은 "다른 프로젝트가 점유 중" 으로 표시하지 않는다 */
                                      occupancyExcludeProjectId={projectId}
                                      onSelectionChange={(list) => setPendingEntryPoints(list)}
                                      onRemovalChange={(list) => setPendingRemovals(list)} />
            </SuspensePopup>
            <EntryPointPreviewPopup show={showEntryPointAndGetId != null} spotId={showEntryPointAndGetId} onClose={() => setShowEntryPointAndGetId(undefined)} />
            <SuspensePopup
                title={"미리보기"}
                visible={showPreviewPopupAndGetId}
                onClose={() => setShowPreviewPopupAndGetId(undefined)}
            >
                <BenefitPreview id={showPreviewPopupAndGetId} />
            </SuspensePopup>
        </div>
    )
}

export default ProjectDetailSummary